import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  Text,
  LoadingOrbit,
  Checkbox,
  TooltipTrigger,
  Tooltip,
  DocsAnnotation,
} from '@chatsdet/ui';
import { Info } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { normalizeConcreteWorkflowId } from './workflowIds';

type SetupNodeConfigProps = {
  isActive: boolean;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  workflowId?: string;
  onImmediateUpdate?: (data: Record<string, any>) => void;
};

interface SetupFlow {
  id: string;
  name: string;
  snapshotVersion: number;
  publishedAt: string | null;
}

interface Snapshot {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
}

export const SetupNodeConfig: React.FC<SetupNodeConfigProps> = ({
  isActive,
  localData,
  setLocalData,
  workflowId,
  onImmediateUpdate,
}) => {
  const setupFlowId = normalizeConcreteWorkflowId(localData.setupFlowId);
  const hasConcreteSetupFlow = setupFlowId !== null;

  // Fetch available promoted flows for the org (excluding current workflow)
  const setupFlowsQ = useQuery({
    queryKey: ['setup-flows', workflowId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (workflowId) params.set('excludeWorkflowId', workflowId);
      const r = await fetch(`${API_URL}/workflows/setup?${params.toString()}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch setup flows');
      const data = await r.json();
      return (data.workflows || []) as SetupFlow[];
    },
    enabled: isActive,
  });

  // Fetch available snapshots for the selected setup flow
  const snapshotsQ = useQuery({
    queryKey: ['node-config-snapshots', setupFlowId],
    queryFn: async () => {
      if (!setupFlowId) return [];

      const r = await fetch(`${API_URL}/workflows/${setupFlowId}/snapshots`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch snapshots');
      const data = await r.json();
      return (data.snapshots || []) as Snapshot[];
    },
    enabled: isActive && hasConcreteSetupFlow,
  });

  const flowOptions = (setupFlowsQ.data || []).map((flow) => ({
    id: flow.id,
    label: flow.name,
  }));

  const snapshotOptions = [
    { id: 'latest', label: 'Live (latest definition)' },
    ...(snapshotsQ.data || []).map((snapshot) => ({
      id: String(snapshot.version),
      label: `v${snapshot.version} (${new Date(snapshot.publishedAt).toLocaleDateString()})`,
    })),
  ];

  return (
    <DocsAnnotation slug="reference/flows" title="Setup Nodes" className="min-h-full">
      <div className="space-y-4">
        <Text className="text-sm text-muted-foreground">
          Select a setup flow to create shared data or dependencies before this workflow runs.
        </Text>

        {setupFlowsQ.isLoading ? (
          <LoadingOrbit />
        ) : flowOptions.length === 0 ? (
          <div className="p-4 rounded-lg bg-muted/50 space-y-2">
            <Text size="sm" className="text-muted-foreground font-medium">
              No promoted flows available.
            </Text>
            <Text size="sm" className="text-muted-foreground">
              Promote a flow first, then it will appear here for use as a setup step.
            </Text>
          </div>
        ) : (
          <>
            <Select
              label="Setup Flow"
              placeholder="Select a setup flow"
              items={flowOptions}
              selectedKey={setupFlowId}
              onSelectionChange={(key) => {
                const flow = setupFlowsQ.data?.find((f) => f.id === key);
                const newData = {
                  ...localData,
                  setupFlowId: key as string,
                  setupFlowName: flow?.name ?? null,
                  // Default to "live" (null = always use the latest definition)
                  snapshotVersion: null,
                };
                setLocalData(newData);
                onImmediateUpdate?.(newData);
              }}
            />

            {hasConcreteSetupFlow && snapshotsQ.isLoading && <LoadingOrbit />}

            {hasConcreteSetupFlow && !snapshotsQ.isLoading && snapshotOptions.length > 0 && (
              <Select
                label="Version"
                placeholder="Select a version"
                items={snapshotOptions}
                selectedKey={
                  localData.snapshotVersion === null || localData.snapshotVersion === undefined
                    ? 'latest'
                    : String(localData.snapshotVersion)
                }
                onSelectionChange={(key) => {
                  setLocalData({
                    ...localData,
                    snapshotVersion: key === 'latest' ? null : Number(key),
                  });
                }}
              />
            )}

            {hasConcreteSetupFlow && localData.snapshotVersion !== undefined && (
              <div className="flex items-center gap-2">
                <Checkbox
                  isSelected={localData.executionMode === 'suite'}
                  onChange={(checked) => {
                    setLocalData({
                      ...localData,
                      executionMode: checked ? 'suite' : 'inline',
                    });
                  }}
                  label="Run once per suite"
                />
                <TooltipTrigger delay={100}>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info size={14} />
                  </button>
                  <Tooltip>
                    Runs once, shares created data across all workflows in the suite.
                  </Tooltip>
                </TooltipTrigger>
              </div>
            )}

            {hasConcreteSetupFlow && localData.executionMode === 'suite' && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50">
                <Info size={12} className="text-muted-foreground mt-0.5 shrink-0" />
                <Text size="xs" className="text-muted-foreground">
                  Runs once per suite. Created data will be shared across all workflows that include
                  this setup.
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </DocsAnnotation>
  );
};
